import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  CreateLabTestRequestT,
  SubmitLabOrderRequestT,
} from '@lotushealth/lotushealth_js_sdk/schemas';
import { toast } from 'sonner';

import { isAxiosError } from 'axios';

import { labsKeys, useCreateLabTestMutation, useSubmitLabOrderMutation } from '@/entities/labs';
import { extractApiErrorDetail } from '@/shared';
import { useSubmissionOrchestrator } from '@/shared/lib/submission';

export type LabOrderSubmissionStatus =
  | 'idle'
  | 'creating-test'
  | 'submitting-order'
  | 'success'
  | 'failed-create-test'
  | 'failed-submit-order'
  | 'failed-missing-patient-info';

export interface LabOrderSubmissionState {
  status: LabOrderSubmissionStatus;
  /** ID of the lab test (either existing or newly created) */
  labTestId: string | null;
  /** Remote order ID from Vital (for confirmation screen) */
  remoteOrderId: string | null;
  errorMessage: string | null;
}

const INITIAL_STATE: LabOrderSubmissionState = {
  status: 'idle',
  labTestId: null,
  remoteOrderId: null,
  errorMessage: null,
};
const SUBMITTING_STATUSES: ReadonlySet<LabOrderSubmissionStatus> = new Set([
  'creating-test',
  'submitting-order',
]);

export function notifyLabOrderSubmissionMessage(message: string | null | undefined): void {
  if (message === null || message === undefined || message.trim() === '') {
    return;
  }

  toast.info('Consider grouping labs', {
    description: message,
  });
}

export interface CreateTestArgs {
  data: CreateLabTestRequestT;
}

export interface SubmitOrderArgs {
  userId: string;
  labTestId: string;
  data: Omit<SubmitLabOrderRequestT, 'labTestId'> & { icdCodes?: string[] };
}

export interface CreateTestAndSubmitOrderArgs {
  userId: string;
  testData: CreateLabTestRequestT;
  orderData: Omit<SubmitLabOrderRequestT, 'labTestId'> & { icdCodes?: string[] };
}

export interface LabOrderSubmissionOrchestrator {
  /** Current submission state */
  state: LabOrderSubmissionState;

  /** Whether the draft is locked (lab test created, awaiting order submission) */
  isDraftLocked: boolean;

  /** Whether a submission operation is in progress */
  isSubmitting: boolean;

  /** Create a custom lab test (returns the created lab test ID) */
  createTest: (args: CreateTestArgs) => Promise<{ labTestId: string } | null>;

  /** Submit a lab order for an existing or newly created test */
  submitOrder: (args: SubmitOrderArgs) => Promise<{ remoteOrderId: string } | null>;

  /** Create a custom test and submit the order in one call */
  createTestAndSubmitOrder: (
    args: CreateTestAndSubmitOrderArgs
  ) => Promise<{ labTestId: string; remoteOrderId: string } | null>;

  /** Reset to initial idle state */
  reset: () => void;
}

export function useLabOrderSubmissionOrchestrator(): LabOrderSubmissionOrchestrator {
  const queryClient = useQueryClient();
  const createLabTestMutation = useCreateLabTestMutation();
  const submitLabOrderMutation = useSubmitLabOrderMutation();
  const { state, isDraftLocked, isSubmitting, runPhase, reset } =
    useSubmissionOrchestrator<LabOrderSubmissionState>({
      initialState: INITIAL_STATE,
      feature: 'lab-order-form',
      submittingStatuses: SUBMITTING_STATUSES,
      isDraftLocked: (current) => current.labTestId !== null,
    });

  const invalidateOrderQueries = useCallback(
    async (userId: string) => {
      await queryClient.invalidateQueries({
        queryKey: labsKeys.patientOrders(userId),
      });
    },
    [queryClient]
  );

  const createTest = useCallback(
    async (args: CreateTestArgs): Promise<{ labTestId: string } | null> => {
      return runPhase({
        pendingStatus: 'creating-test',
        execute: async () => {
          const response = await createLabTestMutation.mutateAsync({
            data: args.data,
          });

          const labTestId = response.labTest.id;
          if (labTestId === null || labTestId === undefined) {
            throw new Error('Create lab test response missing labTest.id.');
          }

          return { labTestId };
        },
        getSuccessPatch: (result) => ({ labTestId: result.labTestId }),
        getError: (error) => ({
          status: 'failed-create-test',
          message: extractApiErrorDetail(error) ?? 'Could not create lab test.',
          operation: 'create-test',
          logMessage: 'lab order create test failed',
        }),
      });
    },
    [createLabTestMutation, runPhase]
  );

  const submitOrder = useCallback(
    async (args: SubmitOrderArgs): Promise<{ remoteOrderId: string } | null> => {
      return runPhase({
        pendingStatus: 'submitting-order',
        execute: async () => {
          const request: SubmitLabOrderRequestT = {
            ...args.data,
            labTestId: args.labTestId,
          };

          const response = await submitLabOrderMutation.mutateAsync({
            userId: args.userId,
            data: request,
          });

          const remoteOrderId = response.order.remote_id;
          if (remoteOrderId === null || remoteOrderId === undefined) {
            throw new Error('Submit lab order response missing order.remote_id.');
          }

          await invalidateOrderQueries(args.userId);
          notifyLabOrderSubmissionMessage(response.message);
          return { remoteOrderId };
        },
        getSuccessPatch: (result) => ({
          status: 'success',
          remoteOrderId: result.remoteOrderId,
        }),
        getError: (error) => {
          const message = extractApiErrorDetail(error) ?? 'Could not submit lab order.';
          const isMissingPatientInfo =
            isAxiosError(error) &&
            error.response?.status === 422 &&
            message.toLowerCase().includes('missing');
          const status: LabOrderSubmissionStatus = isMissingPatientInfo
            ? 'failed-missing-patient-info'
            : 'failed-submit-order';
          return {
            status,
            message,
            operation: 'submit-order',
            logMessage: 'lab order submit failed',
          };
        },
      });
    },
    [invalidateOrderQueries, runPhase, submitLabOrderMutation]
  );

  const createTestAndSubmitOrder = useCallback(
    async (
      args: CreateTestAndSubmitOrderArgs
    ): Promise<{ labTestId: string; remoteOrderId: string } | null> => {
      const created = await createTest({ data: args.testData });
      if (!created) {
        return null;
      }

      const submitted = await submitOrder({
        userId: args.userId,
        labTestId: created.labTestId,
        data: args.orderData,
      });

      if (!submitted) {
        return null;
      }

      return { labTestId: created.labTestId, remoteOrderId: submitted.remoteOrderId };
    },
    [createTest, submitOrder]
  );

  return {
    state,
    isDraftLocked,
    isSubmitting,
    createTest,
    submitOrder,
    createTestAndSubmitOrder,
    reset,
  };
}
